import { useEffect, useCallback, useState } from "react";
import cn from "classnames";

// components
import {
  Button,
  MultiSelectCheckbox,
  Radio,
  Input,
  SearchInput,
} from "components/LibraryComponents/Components";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// types
import { Generic } from "types/filters-store.types";
import { SectionsProps } from "pages/BusinessProfileEdit/BusinessProfileEdit";

// constants
import {
  countryOptions,
  activityOptions,
  relationshipOptions,
} from "constants/BusinessProfileEdit";

// utils
import { getHighlightedText } from "utils/advanced-search";
import { saveErrorToast } from "utils/edit-profile";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// fetch
import { updateExtendedProfileData } from "data/fetch";

// styles
import styles from "./ExportSection.module.scss";

export default function ExportSection({ setUnsavedChanges }: SectionsProps) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  // State initialization
  const [countryCheckboxes, setCountryCheckboxes] =
    useState<Generic[]>(countryOptions);
  const [search, setSearch] = useState<string>("");
  const [exporter, setExporter] = useState<boolean | string>(false);
  const [activities, setActivities] = useState<Generic[]>([]);
  const [relationships, setRelationships] = useState<Generic[]>([]);
  const [other, setOther] = useState<string>("");
  const [countries, setCountries] = useState<Generic[]>([]);
  const [savingFields, setSavingFields] = useState<{ [key: string]: boolean }>(
    {}
  ); // Tracks saving state per section

  const EXPORTER = "Exporter" as const;
  const BUSINESS_ACTIVITIES = "Business activities" as const;
  const DESIRED_BUSINESS_RELATIONSHIPS = "Desired business relationships" as const;
  const COUNTRIES = "Countries" as const;

  const {
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = useSaveConfirmation<typeof EXPORTER | typeof BUSINESS_ACTIVITIES | typeof DESIRED_BUSINESS_RELATIONSHIPS | typeof COUNTRIES>();

  const addTask = useMeiliTaskToast();

  // Parse export_business_activities, desired_export_business_relationship and exporting_to from their respective formats
  const parseMultiSelect = (
    value: string | null,
    options: Generic[]
  ): Generic[] =>
    value
      ? value
          .split(/[;]/) // Split on semicolon (handles both ";" and "; " formats)
          .map((v) => {
            const trimmed = v.trim();
            const option = options.find(
              (opt) => opt.value === trimmed || opt.label === trimmed
            );
            return option || { value: trimmed, label: trimmed };
          })
          .filter((item): item is Generic => !!item.value)
      : [];

  // Format multi-select arrays to semicolon-space-separated labels for payload (except countries which need semicolon-only)
  const formatMultiSelect = (items: Generic[], isCountries: boolean): string =>
    items
      .map((item) => (isCountries ? item.value : item.label))
      .join(isCountries ? ";" : "; ");

  // Section-specific change detection
  const hasExporterChanged = () =>
    extendedProfile?.exporter_status !==
    (exporter === true ? "Y" : exporter === false ? "N" : "W");

  const hasActivitiesChanged = () => {
    if (!exporter) return false; // No activities if not an exporter
    if (!extendedProfile?.export_business_activities && !formatMultiSelect(activities, false)) return false; // allow null and an empty string to be considered unchanged 
    return extendedProfile?.export_business_activities !==
      formatMultiSelect(activities, false);
  }

  const otherSelected = relationships.some(
    (item) => item.value === "relationships/other"
  );

  const hasRelationshipsChanged = () => {
    if (!exporter) return false; // No relationships if not an exporter
    // Filter out "Other" to match what’s stored in extendedProfile
    const filteredRelationships = relationships.filter(
      (item) => item.value !== "relationships/other"
    );

    return (
      (extendedProfile?.desired_export_business_relationship ?? "") !==
        formatMultiSelect(filteredRelationships, false) ||
      (extendedProfile?.export_objectives_description ?? "") !==
        (otherSelected && other.trim() ? other : "")
    );
  };

  const hasCountriesChanged = () => {
    if (!exporter) return false; // No countries if not an exporter
    if (!extendedProfile?.exporting_to && !formatMultiSelect(countries, true)) return false // allow null and an empty string to be considered unchanged
    return extendedProfile?.exporting_to !== formatMultiSelect(countries, true);
  }

  // Check if any field has changed for global unsaved changes
  const hasAnyFieldChanged = useCallback(() =>
    hasExporterChanged() ||
    hasActivitiesChanged() ||
    hasRelationshipsChanged() ||
    hasCountriesChanged(), [extendedProfile, exporter, activities, relationships, other, countries]);

  // Initialize state from extendedProfile
  useEffect(() => {
    if (extendedProfile) {
      const newExporter =
        extendedProfile.exporter_status === "Y"
          ? true
          : extendedProfile.exporter_status === "N"
          ? false
          : "maybe";

      const newActivities = parseMultiSelect(
        extendedProfile.export_business_activities,
        activityOptions
      );

      const newRelationships = parseMultiSelect(
        extendedProfile.desired_export_business_relationship,
        relationshipOptions
      );

      const newOther = extendedProfile.export_objectives_description || "";

      if (newOther) {
        newRelationships.push({
          value: "relationships/other",
          label: "Other",
        });
      }

      const newCountries = parseMultiSelect(
        extendedProfile.exporting_to,
        countryOptions
      );

      setExporter(newExporter);
      setActivities(newActivities);
      setRelationships(newRelationships);
      setOther(newOther);
      setCountries(newCountries);
    }
  }, [extendedProfile]);

  // Update country checkboxes based on search
  useEffect(() => {
    const filteredCountries = countryOptions.filter((country) =>
      country.label.toLowerCase().includes(search.trim().toLowerCase())
    );

    setCountryCheckboxes(getHighlightedText(filteredCountries, search));
  }, [search]);

  // Update unsaved changes state
  useEffect(() => {
    setUnsavedChanges(hasAnyFieldChanged());
    return () => {
      setUnsavedChanges(false);
    };
  }, [
    exporter,
    activities,
    relationships,
    other,
    countries,
    setUnsavedChanges,
  ]);

  useEffect(() => {
    if (hasAnyFieldChanged()) {
      clearConfirmation();
    }
  }, [hasAnyFieldChanged, clearConfirmation]);

  // Save handler for Exporter
  const handleSaveExporter = async () => {
    if (!hasExporterChanged() || savingFields.exporter) return;

    setSavingFields((prev) => ({ ...prev, exporter: true }));

    try {
      const newExporterStatus =
        exporter === true ? "Y" : exporter === false ? "N" : "W";

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({ exporter_status: newExporterStatus })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        exporter_status: newExporterStatus,
      });

      const responseData = await response.json();
      addTask({
        taskUid: responseData?.taskUid,
        label: EXPORTER,
        status: responseData?.status,
      });
      showConfirmation(EXPORTER);

      if (exporter === false) {
        setActivities([]);
        setRelationships([]);
        setOther("");
        setCountries([]);
      }
    } catch (error: any) {
      console.error(`Error saving exporter:`, error);
      saveErrorToast();
    } finally {
      setSavingFields((prev) => ({ ...prev, exporter: false }));
    }
  };

  // Save handler for Activities
  const handleSaveActivities = async () => {
    if (!hasActivitiesChanged() || savingFields.activities) return;

    setSavingFields((prev) => ({ ...prev, activities: true }));

    try {
      const exportBusinessActivities = formatMultiSelect(activities, false);

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({ export_business_activities: exportBusinessActivities })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        export_business_activities: exportBusinessActivities,
      });

      const responseData = await response.json();

      showConfirmation(BUSINESS_ACTIVITIES);
      addTask({
        taskUid: responseData?.taskUid,
        label: BUSINESS_ACTIVITIES,
        status: responseData?.status,
      });

    } catch (error: any) {
      console.error(`Error saving business activities:`, error);
      saveErrorToast();
    } finally {
      setSavingFields((prev) => ({ ...prev, activities: false }));
    }
  };

  // Save handler for Relationships and Other
  const handleSaveRelationships = async () => {
    if (!hasRelationshipsChanged() || savingFields.relationships) return;

    setSavingFields((prev) => ({ ...prev, relationships: true }));
    try {
      // Filter out "Other" since it's not an allowed server value
      const filteredRelationships = relationships.filter(
        (item) => item.value !== "relationships/other"
      );

      const desiredExportBusinessRelationship = formatMultiSelect(
        filteredRelationships,
        false
      );

      const exportObjectivesDescription = otherSelected ? other : "";

      const payload: any = {
        desired_export_business_relationship: desiredExportBusinessRelationship,
      };

      // Only include export_objectives_description if "Other" is selected and has content
      if (otherSelected && exportObjectivesDescription.trim()) {
        payload.export_objectives_description = exportObjectivesDescription;
      }

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify(payload)
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        desired_export_business_relationship: desiredExportBusinessRelationship,
        export_objectives_description: exportObjectivesDescription,
      });

      const responseData = await response.json();
      showConfirmation(DESIRED_BUSINESS_RELATIONSHIPS);
      addTask({
        taskUid: responseData?.taskUid,
        label: DESIRED_BUSINESS_RELATIONSHIPS,
        status: responseData?.status,
      });

    } catch (error: any) {
      console.error(`Error saving business relationship:`, error);
      saveErrorToast();
    } finally {
      setSavingFields((prev) => ({ ...prev, relationships: false }));
    }
  };

  // Save handler for Countries
  const handleSaveCountries = async () => {
    if (!hasCountriesChanged() || savingFields.countries) return;

    setSavingFields((prev) => ({ ...prev, countries: true }));
    try {
      const exportingTo = formatMultiSelect(countries, true);

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({ exporting_to: exportingTo })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        exporting_to: exportingTo,
      });

      const responseData = await response.json();

      showConfirmation(COUNTRIES);
      addTask({
        taskUid: responseData?.taskUid,
        label: COUNTRIES,
        status: responseData?.status,
      });

    } catch (error: any) {
      console.error(`Error saving export to countries:`, error);
      saveErrorToast();
    } finally {
      setSavingFields((prev) => ({ ...prev, countries: false }));
    }
  };

  return (
    <div className={styles["export-section-root"]}>
      <h2 className={styles["heading"]}>Export information</h2>
      <div className={cn(styles["section-content"], styles["space-y-6"])}>
        <div>
          <h3 className={styles["title"]}>Exporter</h3>
          <p className={styles["desc"]}>
            Export information is used to help find small businesses that export
            to other countries. If you are currently exporting, or interested in
            exporting, entering this information may help you expand your export
            business.
          </p>
        </div>
        <div className={styles["space-y-4"]}>
          <Radio
            id={"exporter-no"}
            name={"exporter"}
            label={"No"}
            className={styles["radio-btn"]}
            checked={exporter === false}
            onChange={() => setExporter(false)}
          />
          <Radio
            id={"exporter-yes"}
            name={"exporter"}
            label={"Yes"}
            className={styles["radio-btn"]}
            checked={exporter === true}
            onChange={() => setExporter(true)}
          />
          <Radio
            id={"exporter-maybe"}
            name={"exporter"}
            label={"Maybe"}
            className={styles["radio-btn"]}
            checked={exporter === "maybe"}
            onChange={() => setExporter("maybe")}
          />
        </div>

        <ResponsiveStack>
          <Button
            type="button"
            loading={savingFields.exporter}
            disabled={!hasExporterChanged() || savingFields.exporter}
            onClick={handleSaveExporter}
          >
            {savingFields.exporter ? "Saving..." : "Save changes"}
          </Button>
          {lastSavedSection === EXPORTER && <Saved />}
        </ResponsiveStack>
      </div>

      {exporter && (
        <>
          <div className={cn(styles["section-content"], styles["space-y-6"])}>
            <h3 className={styles["title"]}>Business Activities</h3>
            <MultiSelectCheckbox
              className={styles["multi-select"]}
              items={activityOptions}
              checkedItems={activities}
              handleChange={setActivities}
              hideOnly
            />

            <ResponsiveStack>
              <Button
                type="button"
                loading={savingFields.activities}
                disabled={!hasActivitiesChanged() || savingFields.activities}
                onClick={handleSaveActivities}
              >
                {savingFields.activities ? "Saving..." : "Save changes"}
              </Button>
              {lastSavedSection === BUSINESS_ACTIVITIES && <Saved />}
            </ResponsiveStack>
          </div>

          <div className={cn(styles["section-content"], styles["space-y-6"])}>
            <h3 className={styles["title"]}>Desired business relationships</h3>
            <div className={styles["space-y-2"]}>
              <MultiSelectCheckbox
                className={styles["multi-select"]}
                items={relationshipOptions}
                checkedItems={relationships}
                handleChange={setRelationships}
                hideOnly
              />

              {otherSelected && (
                <div
                  className={cn(styles["other-wrapper"], styles["space-y-2"])}
                >
                  <p>
                    <strong>Other export objective</strong>
                  </p>
                  <Input
                    id="type-other"
                    name="other"
                    type="text"
                    placeholder="Type other export objective here"
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                  />
                </div>
              )}
            </div>

            <ResponsiveStack>
              <Button
                type="button"
                loading={savingFields.relationships}
                disabled={
                  !hasRelationshipsChanged() || savingFields.relationships
                }
                onClick={handleSaveRelationships}
              >
                {savingFields.relationships ? "Saving..." : "Save changes"}
              </Button>
              {lastSavedSection === DESIRED_BUSINESS_RELATIONSHIPS && <Saved />}
            </ResponsiveStack>
          </div>
          <div className={cn(styles["section-content"], styles["space-y-6"])}>
            <div>
              <h3 className={styles["title"]}>Countries</h3>
              <p className={styles["desc"]}>
                Before selecting countries, please check{" "}
                <a
                  href="#"
                  target="_blank"
                  rel="noreferrer"
                  className="usa-link usa-link--external"
                >
                  SBA’s Business Guide for Import and Export Laws and
                  Regulations
                </a>
                .
              </p>
            </div>
            <SearchInput
              id="search-input-small-default"
              name="search-input-small-default"
              value={search}
              placeholder="Type to search for a country"
              inputSize="small"
              onChange={(e) => setSearch(e.target.value)}
              handleClear={() => {
                setSearch("");
                setCountryCheckboxes(countryOptions);
              }}
            />
            <div
              className={cn(
                styles["multi-select-container"],
                styles["space-y-4"]
              )}
            >
              <p className={styles["countries-label"]}>COUNTRIES</p>
              <MultiSelectCheckbox
                className={styles["multi-select"]}
                items={countryCheckboxes}
                checkedItems={countries}
                handleChange={setCountries}
                isInnerHtml
              />
            </div>

            <ResponsiveStack>
              <Button
                type="button"
                loading={savingFields.countries}
                disabled={!hasCountriesChanged() || savingFields.countries}
                onClick={handleSaveCountries}
              >
                {savingFields.countries ? "Saving..." : "Save changes"}
              </Button>
              {lastSavedSection === COUNTRIES && <Saved />}
            </ResponsiveStack>
          </div>
        </>
      )}
    </div>
  );
}
